// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * The web's signal that the explicit health/AI consent is missing (Apple 5.1.2(i), GDPR Art. 9(2)(a)).
 *
 * Two things raise it: `/v1/me` says `healthAiConsent` is null for a completed account (the next-start gate,
 * RequireOnboardingComplete), and ANY endpoint answering 403 HEALTH_AI_CONSENT_REQUIRED (createApiClient
 * passes `signalHealthConsentRequired` to the contracts client). The consent screen clears it. While it is
 * raised, AppShell renders the consent gate INSTEAD of the app (no Today/Plan/Kitchen/Amy chrome), and the
 * onboarding flow falls back to its consent interstitial — a refusal is never shown as a generic error.
 *
 * Module state, not React state, because the API client that raises it lives outside the component tree.
 */
import { useSyncExternalStore } from "react"

/**
 * The one screen that stays reachable without the consent: Privacy & data (export, deletion, how to withdraw).
 * Both are allowed by the server without consent, so the UI must not lock a person who does NOT want to
 * consent out of their own data. Every other route stays behind the gate. Same path the More hub links to.
 */
export const PRIVACY_DATA_PATH = "/app/more/privacy"

/** True for the Privacy & data route, with or without a trailing slash. */
export function isPrivacyDataPath(pathname: string): boolean {
  return pathname === PRIVACY_DATA_PATH || pathname === `${PRIVACY_DATA_PATH}/`
}

let required = false
/**
 * True from a successful in-app withdrawal until the consent is given again. Raising the gate swaps the shell's frame,
 * which remounts Privacy & data — this flag lets the fresh page still say "you have withdrawn" instead of "you have not consented".
 */
let withdrawn = false
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

export function isHealthConsentRequired(): boolean {
  return required
}

export function signalHealthConsentRequired(): void {
  if (required) return
  required = true
  emit()
}

export function markHealthConsentWithdrawn(): void {
  withdrawn = true
}

export function wasHealthConsentWithdrawn(): boolean {
  return withdrawn
}

export function clearHealthConsentRequired(): void {
  withdrawn = false
  if (!required) return
  required = false
  emit()
}

export function onHealthConsentRequiredChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** React view of the signal. */
export function useHealthConsentRequired(): boolean {
  return useSyncExternalStore(onHealthConsentRequiredChange, isHealthConsentRequired, () => false)
}
